import { createElement as rc, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { DropdownList as _DropdownList, ListItem, Menu, RowDetailStyle } from './styles';
import { nativeOnlyProperties, webOnlyProperties } from 'lib_ui-services';
const noop = () => {};
function DropDownList(props) {
    const {
        displayUnassignedRow,
        alwaysDisplayUnassignedRow,
        getItemProps,
        menuProps,
        highlightedIndex,
        hNode,
        id,
        inputIsDirty,
        isOpen,
        isSearching,
        listItemToString,
        records: _records,
        recordCount: _recordCount,
        selectedValues,
        unassignedValue,
        emptyListValue,
        localOnlyNoticeValue,
        value,
        closeMenu,
        setHighlightedIndex,
        onBlur: _onBlur,
        onFocus,
        width
    } = props || {};

    const { mobile, listLineHeight } = useTheme();

    // recalculate num lines for each item if scrollbar appears
    const [isScrollable, setIsScrollable] = useState(false);
    const listRef = useRef(null);
    useEffect(() => {
        if (listRef.current && listRef.current.resetAfterIndex) {
            listRef.current.resetAfterIndex(0);
        }
    }, [isScrollable, width]);

    let recordCount;
    let records;
    // Show searching indicator while a fullTextSearch is in progress
    // This prevents showing stale records from a previous search
    if (isSearching && emptyListValue) {
        records = [emptyListValue];
        recordCount = 1;
    // Add in the "Unassigned Row" if appropriate
    // Either using `alwaysDisplayUnassignedRow` to always display it (e.g. in a multi picker, for data rights)
    // of using `displayUnassignedRow` in a regular dropdown to allow clearing the selection.
    } else if (
        alwaysDisplayUnassignedRow ||
        (displayUnassignedRow && value != null && !value.isDefaultRecord && !inputIsDirty)
    ) {
        records = [unassignedValue, ..._records];
        recordCount = _recordCount + 1;
    } else if (emptyListValue && !_records?.length > 0) {
        records = [emptyListValue];
        recordCount = 1;
    } else {
        records = _records;
        recordCount = _recordCount;
    }

    // #2047: when there ARE options, the notice goes after them, so the caveat is read in the same
    // place the list runs out. When there are none, `emptyListValue` is already the notice (the
    // caller substitutes it), so the `includes` guard stops it appearing twice. Not while
    // searching: that branch is showing "Searching..." and has no answer to caveat yet.
    if (!isSearching && localOnlyNoticeValue && records?.length > 0 && !records.includes(localOnlyNoticeValue)) {
        records = [...records, localOnlyNoticeValue];
        recordCount = recordCount + 1;
    }

    const RowDetail = useMemo(() => {
        function _RowDetail({ item, theme }) {
            let displayText = listItemToString(item);
            let disabled = false;
            if (selectedValues?.some(value => value._id === item._id)) {
                displayText = '✓ ' + displayText;
                disabled = true;
            }
            return rc(RowDetailStyle, { disabled, theme, title: displayText }, displayText);
        }

        _RowDetail.getItemSize = index => {
            const displayText = listItemToString(records[index]);
            // I couldn't get `theme` in this function, so hardcoding for now...
            const pixelsPerLineOfText = 17;
            const paddingPerRow = mobile ? 32 : 16;
            const scrollbarWidth = 10;
            const widthWithPossibleScrollbar = isScrollable ? width - scrollbarWidth : width;
            const numLines = getNumLines(displayText, widthWithPossibleScrollbar - 10);
            return pixelsPerLineOfText * numLines + paddingPerRow;
        };
        return _RowDetail;
    }, [isScrollable, listItemToString, mobile, records, selectedValues, width]);

    const Row = useCallback(
        props => {
            const { children, rowIndex, ...otherProps } = props;
            const onFocus = () => {
                setHighlightedIndex(rowIndex);
            };
            if (selectedValues?.some(value => value._id === otherProps['data-id'])) {
                return rc(ListItem, { ...otherProps, onClick: noop, onFocus, rowIndex }, children);
            }
            return rc(ListItem, { ...otherProps, onFocus, rowIndex }, children);
        },
        [setHighlightedIndex, selectedValues]
    );

    // Allow the user to select an item from the list by clicking 'Enter' while it is highlighted
    const _getItemProps = useCallback(
        (...args) => {
            const itemProps = {
                ...getItemProps(...args)
            };
            const onKeyDown = e => {
                if (e.key === 'Enter') {
                    itemProps.onClick(e);
                }
            };
            itemProps.onKeyDown = onKeyDown;
            return itemProps;
        },
        [getItemProps]
    );

    // Close the list when the user goes to a control the dropdown does not contain
    const onBlur = useCallback(
        e => {
            if (e.relatedTarget == null || !e.currentTarget.contains(e.relatedTarget)) {
                closeMenu?.();
                _onBlur?.(e);
            }
        },
        [closeMenu, _onBlur]
    );

    const numLinesArray = useMemo(() => {
        const scrollbarWidth = 10;
        const widthWithPossibleScrollbar = isScrollable ? width - scrollbarWidth : width;
        return records.map(record => getNumLines(listItemToString(record), widthWithPossibleScrollbar - 10));
    }, [isScrollable, listItemToString, records, width]);

    const testId = (hNode.testId ?? hNode.id) + '-menu';
    // prettier-ignore
    return rc(Menu, {
        itemCount: recordCount,
        numLinesArray,
        mobile,
        visible: isOpen,
        ...webOnlyProperties({
            'data-testid': testId,
            accessibility: true,
            accessibilityLabel: testId
        }),
        ...nativeOnlyProperties({
            testId
        }),
        ...menuProps,
        onBlur,
        onFocus,
        width
    },
        rc(_DropdownList, {
            id,
            itemCount: recordCount,
            data: records,
            Row,
            RowDetail,
            highlightedIndex,
            selectedItem: value,
            getItemProps: _getItemProps,
            itemHeightPixels: listLineHeight,
            visible: isOpen,
            setIsScrollable,
            ref: listRef
        }
        )
    );
}

DropDownList.displayName = 'DropDownList';
export default DropDownList;

// function to calculate number of lines needed to display text
function getNumLines(text, listWidth) {
    if (__SST_REACT_NATIVE__) return getNumLinesNative(text, listWidth);
    return getNumLinesWeb(text, listWidth);
}

function getNumLinesWeb(text, listWidth) {
    // set up canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '14px Inter, sans-serif';

    // calculate max width
    // couldn't get these from theme...
    const menuPadding = 8;
    const rowPadding = 12;
    const maxWidth = listWidth - (menuPadding * 2 + rowPadding * 2);

    // Split by hyphen or space, capturing the separator in the output array.
    // Example: "one-two three" becomes ["one", "-", "two", " ", "three"]
    const parts = text.split(/(-| )/);

    let lineCount = 1;
    let currentLine = parts[0];

    // Iterate starting from the first separator (index 1), stepping by 2
    for (let i = 1; i < parts.length; i += 2) {
        const delimiter = parts[i]; // the hyphen or space
        const word = parts[i + 1]; // the next word

        const width = context.measureText(currentLine + delimiter + word).width;

        if (width < maxWidth) {
            currentLine += delimiter + word;
        } else {
            lineCount++;
            currentLine = word;
        }
    }

    return lineCount;
}

function getNumLinesNative(text, listWidth) {
    const avgCharWidth = 8;  // 14px Inter
    const delimiterWidth = {
        ' ': 4,
        '-': 4
    };

    // calculate max width
    // couldn't get these from theme...
    const menuPadding = 8;
    const rowPadding = 12;
    const maxWidth = listWidth - (menuPadding * 2 + rowPadding * 2);

    // Split by hyphen or space, capturing the separator in the output array.
    // Example: "one-two three" becomes ["one", "-", "two", " ", "three"]
    const parts = text.split(/(-| )/);

    let lineCount = 1;
    let currentLineWidth = 0;

    // Iterate starting from the first separator (index 1), stepping by 2
    for (let i = 1; i < parts.length; i += 2) {
        const delimiter = parts[i]; // the hyphen or space
        const word = parts[i + 1]; // the next word

        const wordWidth = word.length * avgCharWidth;

        if (currentLineWidth + delimiterWidth[delimiter] + wordWidth < maxWidth) {
            currentLineWidth += delimiterWidth[delimiter] + wordWidth;
        } else {
            lineCount++;
            currentLineWidth = wordWidth;
        }
    }

    return lineCount;
}
